import { forwardRef, useCallback, useEffect, useId, useRef, useState } from 'react';
import axios from 'axios';
import { Check, Globe, Loader2, Phone, PhoneCall, PhoneOff } from 'lucide-react';
import { trackEvent, Events } from '../../lib/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Legacy endpoint (name + phone, generic Agent9 call): used only when the website demo is switched off.
export const TRY_CALL_ENDPOINT = `${API}/growth/try-ai-call`;
// Website demo: learn the visitor's business website, then call as that business's AI assistant.
export const WEBSITE_DEMO = `${API}/demo-calls/website`;

const POLL_MS = 1200;
const POLL_LIMIT_MS = 6 * 60 * 1000;
const LEARNING = ['reading', 'learning', 'preparing'];
const CALLING = ['calling', 'ringing', 'in_call'];
const ENDED = ['completed', 'not_answered', 'failed', 'voice_failed'];

// One config read per page load, shared by every form on the page (inline section,
// floating panel, mobile sheet), so a form opened later renders the right fields at once.
let configCache = null;
let configPromise = null;
function loadConfig() {
  if (!configPromise) {
    configPromise = axios.get(`${WEBSITE_DEMO}/config`)
      .then((r) => r.data || { enabled: false })
      .catch(() => ({ enabled: false }))
      .then((c) => { configCache = c; return c; });
  }
  return configPromise;
}

function errorMessage(err, fallback = 'We could not place the call. Please try again.') {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (status === 429) return 'You have reached the demo call limit for now. Please try again later.';
  if (Array.isArray(detail)) return 'Please check your name, website and phone number.';
  if (!err?.response) return 'We could not reach Agent9. Check your connection and try again.';
  return fallback;
}

// The original form's wording, unchanged.
function legacyErrorMessage(err) {
  if (err?.response?.status === 429) return 'You have reached the demo call limit for now. Please try again in an hour.';
  return errorMessage(err);
}

function hostOf(url) {
  try {
    return new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

function clock(sec) {
  const s = Math.max(0, Math.floor(sec || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** One live step in the learning list. */
function Step({ state, children, testId }) {
  return (
    <li data-testid={testId} data-state={state} className={`a9l-wd-step is-${state}`}>
      <span className="a9l-wd-step__icon" aria-hidden="true">
        {state === 'done' ? <Check size={13} /> : state === 'active' ? <Loader2 size={13} className="a9l-wd-spin" /> : null}
      </span>
      <span>{children}</span>
    </li>
  );
}

/**
 * Live status: "learning your website" with real steps, then the real-time
 * "calling you right now" pill, then how the call went.
 */
function LiveStatus({ testIdPrefix, website, st, elapsed, callSecs, onFallback, fallbackBusy, fallbackError }) {
  const status = st?.status || 'reading';
  const host = hostOf(website);
  const biz = st?.business_name;
  const pages = st?.pages_read || 0;
  const stepState = (doneWhen, activeWhen) => (doneWhen ? 'done' : activeWhen ? 'active' : 'todo');
  const learned = !LEARNING.includes(status) || status === 'preparing';
  const [fbName, setFbName] = useState('');
  const [fbType, setFbType] = useState('general');

  if (LEARNING.includes(status)) {
    return (
      <div data-testid={`${testIdPrefix}-learning`} role="status" aria-live="polite" className="a9l-wd-live">
        <p className="a9l-wd-live__kicker"><span className="a9l-dot a9l-dot--ember" aria-hidden="true" /> Live · {clock(elapsed)}</p>
        <p className="a9l-wd-live__title">Our AI agent is learning your website and business model…</p>
        <ul className="a9l-wd-steps">
          <Step testId={`${testIdPrefix}-step-open`} state={stepState(status !== 'reading' || pages > 0, status === 'reading')}>
            Reading {host}
          </Step>
          <Step testId={`${testIdPrefix}-step-pages`} state={stepState(pages > 0, false)}>
            {pages > 0 ? `Read ${pages} page${pages === 1 ? '' : 's'}` : 'Finding your services, hours and prices'}
          </Step>
          <Step testId={`${testIdPrefix}-step-business`} state={stepState(learned, status === 'learning')}>
            {learned && biz ? <>Learned <strong>{biz}</strong>{st?.industry ? ` · ${st.industry}` : ''}</> : 'Understanding your business model'}
          </Step>
          <Step testId={`${testIdPrefix}-step-prepare`} state={stepState(false, status === 'preparing')}>
            Preparing your AI assistant
          </Step>
        </ul>
        <p className="a9l-note">Your phone rings the moment it's ready. Usually under a minute.</p>
      </div>
    );
  }

  if (CALLING.includes(status) || status === 'completed') {
    const pill =
      status === 'in_call' ? `Connected · ${clock(callSecs)}`
      : status === 'completed' ? `Call finished${st?.duration ? ` · ${clock(st.duration)}` : ''}`
      : status === 'ringing' ? 'Our AI agent is calling you right now · ringing'
      : 'Our AI agent is calling you right now';
    return (
      <div data-testid={`${testIdPrefix}-calling`} role="status" aria-live="assertive" className="a9l-wd-live">
        <div data-testid={`${testIdPrefix}-call-pill`} data-status={status} className={`a9l-wd-pill ${status === 'completed' ? 'is-done' : 'is-live'}`}>
          {status === 'completed' ? <Check size={16} aria-hidden="true" /> : <PhoneCall size={16} aria-hidden="true" className="a9l-wd-ring" />}
          <span>{pill}</span>
        </div>
        {status !== 'completed' ? (
          <p className="a9l-body mt-3" style={{ fontSize: '0.9rem' }}>
            Pick up. It will greet you as the AI assistant for <strong>{biz || 'your business'}</strong>. Ask it what your customers ask.
          </p>
        ) : (
          <p className="a9l-body mt-3" style={{ fontSize: '0.9rem' }}>That's how your AI receptionist could answer every call. <a href="/signup" className="a9l-wd-link">Start your free trial</a>.</p>
        )}
      </div>
    );
  }

  if (status === 'needs_info') {
    return (
      <div data-testid={`${testIdPrefix}-needs-info`} className="grid gap-3">
        <div role="alert" className="a9l-alert a9l-alert--err">{st?.message || 'We couldn’t learn enough from that website.'}</div>
        <p className="a9l-note" style={{ fontSize: '0.82rem' }}>
          Tell us your business name and type and our AI will call using only that. It won't make anything up.
        </p>
        <form
          className="grid gap-3"
          data-testid={`${testIdPrefix}-fallback-form`}
          onSubmit={(e) => { e.preventDefault(); onFallback(fbName, fbType); }}
          noValidate
        >
          <input
            data-testid={`${testIdPrefix}-fallback-name`}
            className="a9l-input" aria-label="Business name" placeholder="Business name"
            value={fbName} onChange={(e) => setFbName(e.target.value)}
          />
          <select
            data-testid={`${testIdPrefix}-fallback-type`}
            className="a9l-input" aria-label="Type of business" value={fbType} onChange={(e) => setFbType(e.target.value)}
          >
            {(st?.industries || []).map((i) => (
              <option key={i.key} value={i.key}>{i.key === 'general' ? 'Other' : i.label.replace(/^./, (c) => c.toUpperCase())}</option>
            ))}
          </select>
          <button type="submit" data-testid={`${testIdPrefix}-fallback-submit`} disabled={fallbackBusy} className="a9l-btn a9l-btn--signal w-full">
            <Phone size={16} aria-hidden="true" /> {fallbackBusy ? 'Preparing…' : 'Call me with this info'}
          </button>
          {fallbackError && <div role="alert" className="a9l-alert a9l-alert--err">{fallbackError}</div>}
        </form>
      </div>
    );
  }

  return (
    <div data-testid={`${testIdPrefix}-ended`} role="alert" className="a9l-alert a9l-alert--err">
      <PhoneOff size={16} aria-hidden="true" className="shrink-0 mt-0.5" />
      <span>{st?.message || 'We couldn’t place the call.'}</span>
    </div>
  );
}

/** The original name + phone form (generic Agent9 call). Kept for the off switch. */
function LegacyForm({ source, testIdPrefix, submitLabel, nameRef }) {
  const uid = useId();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [invalid, setInvalid] = useState({});

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const bad = { name: !name.trim(), phone: phone.replace(/\D/g, '').length < 10 };
    setInvalid(bad);
    if (bad.name || bad.phone) {
      setError(bad.name ? 'Please enter your name.' : 'Please enter a valid phone number, including area code.');
      return;
    }
    setLoading(true);
    trackEvent(Events.TRY_CALL_REQUESTED, { source });
    try {
      const r = await axios.post(TRY_CALL_ENDPOINT, { name: name.trim(), phone: phone.trim() });
      if (r.data?.success === false) setError(r.data?.error || 'We could not place the call. Please try again.');
      else setSuccess(r.data?.message || 'Agent9 is calling you now. Pick up and talk to it like a real customer.');
    } catch (err) {
      setError(legacyErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div data-testid={`${testIdPrefix}-success`} role="status" className="a9l-alert a9l-alert--ok">
        <Check size={16} aria-hidden="true" className="shrink-0 mt-0.5" />
        <span>{success}</span>
      </div>
    );
  }
  return (
    <form data-testid={testIdPrefix === 'try-live' ? 'floating-try-live-form' : `${testIdPrefix}-form`} onSubmit={onSubmit} noValidate className="grid gap-4">
      <div className="a9l-field">
        <label htmlFor={`${uid}-name`}>Your name</label>
        <input ref={nameRef} id={`${uid}-name`} data-testid={`${testIdPrefix}-name-input`} className="a9l-input" type="text"
          name="name" autoComplete="name" placeholder="Alex Rivera" value={name}
          aria-invalid={invalid.name ? 'true' : 'false'} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="a9l-field">
        <label htmlFor={`${uid}-phone`}>Mobile number</label>
        <input id={`${uid}-phone`} data-testid={`${testIdPrefix}-phone-input`} className="a9l-input" type="tel" name="phone"
          inputMode="tel" autoComplete="tel" placeholder="(415) 555-0123" value={phone}
          aria-invalid={invalid.phone ? 'true' : 'false'} onChange={(e) => setPhone(e.target.value)} />
      </div>
      <button type="submit" data-testid={`${testIdPrefix}-submit-btn`} disabled={loading} className="a9l-btn a9l-btn--signal w-full">
        <Phone size={16} aria-hidden="true" />
        {loading ? 'Calling you…' : submitLabel}
      </button>
      {error && <div data-testid={`${testIdPrefix}-error`} role="alert" className="a9l-alert a9l-alert--err">{error}</div>}
      <p className="a9l-note">By submitting, you allow Agent9 to call this number once for a live demo. No card. No spam.</p>
    </form>
  );
}

/**
 * "Experience Your Own AI Receptionist": name + business website + phone + consent.
 * Agent9 learns the website, then calls as that business's AI assistant.
 * A real component (not defined inside a parent render), so inputs keep focus while typing.
 */
const CallForm = forwardRef(function CallForm(
  { source = 'unknown', testIdPrefix = 'try-live', submitLabel = 'Call Me as My Business AI' }, nameRef,
) {
  const uid = useId();
  const [config, setConfig] = useState(configCache); // null = loading; {enabled:false} = legacy form
  const [name, setName] = useState('');
  const [website, setWebsite] = useState('');
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false);
  const [fax, setFax] = useState(''); // honeypot
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [invalid, setInvalid] = useState({});
  const [job, setJob] = useState(null); // {id, token}
  const [st, setSt] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [callSecs, setCallSecs] = useState(0);
  const [fallbackBusy, setFallbackBusy] = useState(false);
  const [fallbackError, setFallbackError] = useState('');
  const tokenRef = useRef({ token: '', at: 0 });
  const startedRef = useRef(0);
  const inCallRef = useRef(0);
  const lastStatusRef = useRef('');

  const fetchToken = useCallback(async () => {
    try {
      const r = await axios.get(`${WEBSITE_DEMO}/form-token`);
      tokenRef.current = { token: r.data?.token || '', at: Date.now() };
    } catch {
      tokenRef.current = { token: '', at: 0 };
    }
  }, []);

  useEffect(() => {
    let alive = true;
    loadConfig().then((c) => { if (alive) setConfig(c); });
    fetchToken();
    return () => { alive = false; };
  }, [fetchToken]);

  // Poll the live status while a request runs.
  useEffect(() => {
    if (!job) return undefined;
    let alive = true;
    let timer;
    const tick = async () => {
      try {
        const r = await axios.get(`${WEBSITE_DEMO}/status/${job.id}`, { params: { t: job.token } });
        if (!alive) return;
        const next = r.data;
        setSt(next);
        if (next.status !== lastStatusRef.current) {
          lastStatusRef.current = next.status;
          trackEvent(Events.TRY_CALL_REQUESTED, { source, step: next.status });
          if (next.status === 'in_call') inCallRef.current = Date.now();
        }
        const done = ENDED.includes(next.status) || next.status === 'needs_info';
        if (!done && Date.now() - startedRef.current < POLL_LIMIT_MS) timer = setTimeout(tick, POLL_MS);
      } catch {
        if (alive && Date.now() - startedRef.current < POLL_LIMIT_MS) timer = setTimeout(tick, POLL_MS * 2);
      }
    };
    tick();
    return () => { alive = false; clearTimeout(timer); };
  }, [job, source]);

  // Live clocks: time spent learning, and time on the call.
  useEffect(() => {
    if (!job) return undefined;
    const t = setInterval(() => {
      setElapsed((Date.now() - startedRef.current) / 1000);
      if (inCallRef.current) setCallSecs((Date.now() - inCallRef.current) / 1000);
    }, 500);
    return () => clearInterval(t);
  }, [job]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading || job) return; // one request at a time
    setError('');
    const digits = phone.replace(/\D/g, '');
    const bad = {
      name: !name.trim(),
      website: !/^(https?:\/\/)?[^\s/$.?#]+\.[^\s]{2,}$/i.test(website.trim()),
      phone: digits.length < 10 || digits.length > 11,
      consent: !consent,
    };
    setInvalid(bad);
    if (bad.name || bad.website || bad.phone || bad.consent) {
      setError(
        bad.name ? 'Please enter your name.'
        : bad.website ? 'Please enter your business website, like www.yourbusiness.com'
        : bad.phone ? 'Please enter a valid mobile number, including area code.'
        : 'Please agree to receive the one AI call.',
      );
      return;
    }
    setLoading(true);
    trackEvent(Events.TRY_CALL_REQUESTED, { source, step: 'submitted' });
    try {
      if (!tokenRef.current.token || Date.now() - tokenRef.current.at > 25 * 60 * 1000) await fetchToken();
      // a person takes longer than 2 s to fill the form; a quick script does not
      const age = Date.now() - tokenRef.current.at;
      if (age < 2100) await new Promise((res) => setTimeout(res, 2100 - age));
      const r = await axios.post(`${WEBSITE_DEMO}/start`, {
        name: name.trim(), website: website.trim(), phone: phone.trim(), consent: true,
        form_token: tokenRef.current.token, fax,
      });
      startedRef.current = Date.now();
      setSt(r.data);
      setJob({ id: r.data.id, token: r.data.status_token });
    } catch (err) {
      setError(errorMessage(err));
      fetchToken();
    } finally {
      setLoading(false);
    }
  };

  const onFallback = async (bizName, bizType) => {
    if (!bizName.trim()) { setFallbackError('Please enter your business name.'); return; }
    setFallbackBusy(true);
    setFallbackError('');
    try {
      const r = await axios.post(`${WEBSITE_DEMO}/${job.id}/fallback`, { business_name: bizName.trim(), business_type: bizType }, { params: { t: job.token } });
      setSt(r.data);
      setJob({ ...job }); // restart polling
    } catch (err) {
      setFallbackError(errorMessage(err, 'We could not prepare the call. Please try again.'));
    } finally {
      setFallbackBusy(false);
    }
  };

  if (config && config.enabled === false) {
    return <LegacyForm source={source} testIdPrefix={testIdPrefix} submitLabel="Call me now" nameRef={nameRef} />;
  }

  if (job) {
    return (
      <LiveStatus testIdPrefix={testIdPrefix} website={website} st={st} elapsed={elapsed} callSecs={callSecs}
        onFallback={onFallback} fallbackBusy={fallbackBusy} fallbackError={fallbackError} />
    );
  }

  return (
    <form data-testid={testIdPrefix === 'try-live' ? 'floating-try-live-form' : `${testIdPrefix}-form`} onSubmit={onSubmit} noValidate className="grid gap-3.5">
      <div className="a9l-field">
        <label htmlFor={`${uid}-name`}>Your name</label>
        <input ref={nameRef} id={`${uid}-name`} data-testid={`${testIdPrefix}-name-input`} className="a9l-input" type="text"
          name="name" autoComplete="name" placeholder="Alex Rivera" maxLength={60} value={name}
          aria-invalid={invalid.name ? 'true' : 'false'} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="a9l-field">
        <label htmlFor={`${uid}-website`}>Business website</label>
        <div className="a9l-wd-iconinput">
          <Globe size={15} aria-hidden="true" />
          <input id={`${uid}-website`} data-testid={`${testIdPrefix}-website-input`} className="a9l-input" type="text"
            name="website" inputMode="url" autoComplete="url" autoCapitalize="none" spellCheck="false"
            placeholder="www.riveraroofing.com" maxLength={300} value={website}
            aria-invalid={invalid.website ? 'true' : 'false'} onChange={(e) => setWebsite(e.target.value)} />
        </div>
      </div>
      <div className="a9l-field">
        <label htmlFor={`${uid}-phone`}>Mobile phone number</label>
        <input id={`${uid}-phone`} data-testid={`${testIdPrefix}-phone-input`} className="a9l-input" type="tel" name="phone"
          inputMode="tel" autoComplete="tel" placeholder="(415) 555-0123" maxLength={20} value={phone}
          aria-invalid={invalid.phone ? 'true' : 'false'} onChange={(e) => setPhone(e.target.value)} />
      </div>
      <div className="a9l-wd-hp" aria-hidden="true">
        <label htmlFor={`${uid}-fax`}>Fax</label>
        <input id={`${uid}-fax`} tabIndex={-1} autoComplete="off" name="fax" value={fax} onChange={(e) => setFax(e.target.value)} />
      </div>
      <label className="a9l-wd-consent" data-invalid={invalid.consent ? 'true' : 'false'}>
        <input type="checkbox" data-testid={`${testIdPrefix}-consent`} checked={consent} onChange={(e) => setConsent(e.target.checked)} />
        <span>{config?.consent_text || 'I agree that Agent9 may place one automated AI phone call to the number above, about the business website I entered. No marketing calls or texts.'}</span>
      </label>
      <button type="submit" data-testid={`${testIdPrefix}-submit-btn`} disabled={loading || !config} className="a9l-btn a9l-btn--signal w-full">
        <Phone size={16} aria-hidden="true" />
        {loading ? 'Checking your website…' : submitLabel}
      </button>
      {error && <div data-testid={`${testIdPrefix}-error`} role="alert" className="a9l-alert a9l-alert--err">{error}</div>}
      <p className="a9l-note">US and Canada numbers. One call, about 3 minutes. No card. No spam.</p>
    </form>
  );
});

export default CallForm;
